import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import { MdAccessTime, MdArrowForwardIos, MdBolt, MdCalendarToday, MdEdit, MdLocalShipping, MdRefresh, MdSearch } from "react-icons/md";
import moment from "moment";
import branchApi from "../../services/branchapi";
import deliveryApi from "../../services/deliveryapi";
import deliverySlotApi from "../../services/deliverySlotApi";
import DeliveryOptionForm from "./DeliveryOptionForm";
import DeliverySlotForm from "./DeliverySlotForm";
import "../../static/css/deliveryConfiguration.css";

const ALL = "ALL";
const money = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
const result = (response) => response?.data?.result || [];
const active = (value) => Number(value) === 1;
const key = (value) => String(value ?? "");
const configBranchId = (row) => row?.branch_id ?? row?.branch?.branchId ?? row?.Branch?.branchId;
const slotBranchId = (row) => row?.branchId ?? row?.branch_id ?? row?.deliveryBranch?.branchId;
const feesOf = (row) => row?.StandardDeliveryFees || row?.standard_delivery_fees || [];
const amountText = (minValue, maxValue) => {
  const min = Number(minValue || 0);
  const max = Number(maxValue || 0);
  return `${money.format(min)} - ${max > 0 ? money.format(max) : "No limit"}`;
};
const timeText = (value) => value ? moment(value, ["HH:mm:ss", "HH:mm"]).format("hh:mm A") : "-";

const DeliveryConfiguration = () => {
  const [branches, setBranches] = useState([]);
  const [allOptions, setAllOptions] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [mode, setMode] = useState(ALL);
  const [search, setSearch] = useState("");
  const [amountKey, setAmountKey] = useState("");
  const [configChoice, setConfigChoice] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [optionModal, setOptionModal] = useState(false);
  const [optionData, setOptionData] = useState(null);
  const [slotModal, setSlotModal] = useState(false);
  const [slotData, setSlotData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [branchResponse, optionResponse, slotResponse] = await Promise.all([
        branchApi.getAllBranches({ limit: 1000, page: 0, status: 1, sortModel: [{ field: "branchName", sort: "asc" }] }),
        deliveryApi.getDeliveryOptions({ limit: 5000, page: 0, branchId: 0, deliTypeId: 0, sortModel: [], filterModel: {} }),
        deliverySlotApi.getDeliverySlots({ limit: 5000, page: 0, branchId: 0, sortModel: [{ field: "fromTime", sort: "asc" }], filterModel: {} }),
      ]);
      const list = result(branchResponse).sort((a, b) => String(a.branchName || "").localeCompare(String(b.branchName || "")));
      setBranches(list);
      setAllOptions(result(optionResponse));
      setAllSlots(result(slotResponse));
      setBranchId((current) => current || (list[0]?.branchId ? String(list[0].branchId) : ""));
    } catch (requestError) {
      console.error("Failed to load delivery management data", requestError);
      setError("Unable to load delivery data. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const values = {};
    branches.forEach((branch) => { values[key(branch.branchId)] = { slots: 0, activeSlots: 0 }; });
    allSlots.forEach((slot) => {
      const id = key(slotBranchId(slot));
      if (!values[id]) values[id] = { slots: 0, activeSlots: 0 };
      values[id].slots += 1;
      if (active(slot.status)) values[id].activeSlots += 1;
    });
    const global = values["0"] || { slots: 0, activeSlots: 0 };
    branches.forEach((branch) => {
      const id = key(branch.branchId);
      if (values[id]?.slots === 0 && global.slots > 0) {
        values[id] = { ...global, inherited: true };
      }
    });
    return values;
  }, [allSlots, branches]);

  const selectedBranch = branches.find((branch) => key(branch.branchId) === key(branchId));
  const branchOptions = useMemo(() => allOptions.filter((row) => key(configBranchId(row)) === key(branchId)), [allOptions, branchId]);
  const ownBranchSlots = useMemo(() => allSlots.filter((row) => key(slotBranchId(row)) === key(branchId)), [allSlots, branchId]);
  const globalSlots = useMemo(() => allSlots.filter((row) => Number(slotBranchId(row)) === 0), [allSlots]);
  const usingGlobalSlots = ownBranchSlots.length === 0 && globalSlots.length > 0;
  const branchSlots = usingGlobalSlots ? globalSlots : ownBranchSlots;
  const optionsByType = useMemo(() => branchOptions.reduce((groups, row) => {
    const type = Number(row.delivery_type_id);
    if (!groups[type]) groups[type] = [];
    groups[type].push(row);
    return groups;
  }, {}), [branchOptions]);

  const selectedConfig = useCallback((type) => {
    const configs = optionsByType[type] || [];
    return configs.find((row) => String(row.id) === String(configChoice[type])) || configs.find((row) => active(row.status)) || configs[0] || null;
  }, [configChoice, optionsByType]);

  const amountGroups = useMemo(() => {
    const groups = new Map();
    branchSlots.forEach((slot) => {
      const min = Number(slot.minAmount || 0);
      const max = Number(slot.maxAmount || 0);
      const id = `${min}-${max}`;
      if (!groups.has(id)) groups.set(id, { id, min, max, slots: [] });
      groups.get(id).slots.push(slot);
    });
    return Array.from(groups.values()).sort((a, b) => a.min - b.min || a.max - b.max);
  }, [branchSlots]);

  useEffect(() => {
    if (!amountGroups.some((group) => group.id === amountKey)) setAmountKey(amountGroups[0]?.id || "");
  }, [amountGroups, amountKey]);
  useEffect(() => { setConfigChoice({}); setAmountKey(""); }, [branchId]);

  const shownBranches = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = term ? branches.filter((branch) => String(branch.branchName || "").toLowerCase().includes(term)) : branches;
    if (mode === "SINGLE") list = list.filter((branch) => key(branch.branchId) === key(branchId));
    return list;
  }, [branches, branchId, mode, search]);

  const express = selectedConfig(2);
  const standard = selectedConfig(4);
  const selectedGroup = amountGroups.find((group) => group.id === amountKey);
  const activeSlots = branchSlots.filter((slot) => active(slot.status));
  const lastUpdated = [...branchOptions, ...branchSlots]
    .map((row) => row.updated_at || row.updatedAt || row.updatedDate || row.created_at || row.createdDate)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))[0];

  const status = (isEnabled, offLabel = "Inactive") => (
    <Badge className={`dm-status ${isEnabled ? "dm-status--active" : "dm-status--inactive"}`}>{isEnabled ? "Active" : offLabel}</Badge>
  );

  const editOption = (config) => {
    if (!config) return;
    setOptionData({ ...config, branch_id: configBranchId(config), StandardDeliveryFees: feesOf(config) });
    setOptionModal(true);
  };
  const addOption = (type) => {
    setOptionData({ branch_id: Number(branchId), delivery_type_id: type, status: 1, barrier_time_before: type === 2 ? 30 : 60, barrier_time_after: type === 2 ? 60 : 120, title: type === 2 ? "Within 30 minutes" : "Standard Delivery", StandardDeliveryFees: [] });
    setOptionModal(true);
  };
  const editSlot = (slot) => {
    setSlotData({ branch_id: Number(branchId), source_branch_id: Number(slotBranchId(slot)), is_inherited: usingGlobalSlots, slots: [{ id: slot.id, from_time: slot.fromTime, to_time: slot.toTime, order_threshold: slot.orderThreshold, min_amount: slot.minAmount, max_amount: slot.maxAmount, status: slot.status }] });
    setSlotModal(true);
  };
  const addSlot = () => {
    setSlotData({ branch_id: Number(branchId), source_branch_id: Number(branchId), is_inherited: false, slots: [{ from_time: "", to_time: "", order_threshold: 30, min_amount: selectedGroup?.min || 0, max_amount: selectedGroup?.max || 0, status: 1 }] });
    setSlotModal(true);
  };

  const methodCard = (type, name, Icon, tone, config) => {
    const configs = optionsByType[type] || [];
    return (
      <div className={`dm-method dm-method--${tone}`}>
        <button type="button" className="dm-method-main" onClick={() => config ? editOption(config) : addOption(type)}>
          <span className="dm-method-icon"><Icon /></span>
          <span className="dm-method-copy">
            <span className="dm-method-title">{name} {status(Boolean(config && active(config.status)), config ? "Inactive" : "Not configured")}</span>
            {config ? <><span><MdAccessTime /> {config.barrier_time_before} - {config.barrier_time_after} min</span><small>{config.title || name}</small><span>{feesOf(config).length} fee slab{feesOf(config).length === 1 ? "" : "s"}</span></> : <small>Click to configure</small>}
          </span>
          <MdArrowForwardIos className="dm-arrow" />
        </button>
        {configs.length > 1 && <Form.Select className="dm-config-select" value={config?.id || ""} onChange={(event) => setConfigChoice((current) => ({ ...current, [type]: event.target.value }))}>{configs.map((item) => <option key={item.id} value={item.id}>{item.title || name} ({active(item.status) ? "Active" : "Inactive"})</option>)}</Form.Select>}
      </div>
    );
  };

  return (
    <div className="delivery-management-page">
      <header className="dm-page-header">
        <div><h2>Delivery Management</h2><p>Configure delivery methods, time slots and capacity for your branches.</p></div>
        <div className="dm-mode-switch"><button type="button" className={mode === ALL ? "active" : ""} onClick={() => setMode(ALL)}>All Branches</button><button type="button" className={mode === "SINGLE" ? "active" : ""} onClick={() => setMode("SINGLE")}>Single Branch</button></div>
      </header>
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? <div className="dm-loading"><Spinner animation="border" /><span>Loading delivery data...</span></div> : (
        <div className="dm-layout">
          <aside className="dm-branches">
            <div className="dm-panel-title"><h3>{mode === ALL ? `Branches (${branches.length})` : "Selected Branch"}</h3><button type="button" onClick={load}><MdRefresh /></button></div>
            {mode === "SINGLE" ? <Form.Select className="dm-single-select" value={branchId} onChange={(event) => setBranchId(event.target.value)}>{branches.map((branch) => <option key={branch.branchId} value={branch.branchId}>{branch.branchName}</option>)}</Form.Select> : <div className="dm-search"><MdSearch /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search branches..." /></div>}
            <div className="dm-branch-list">{shownBranches.map((branch) => { const branchStat = stats[key(branch.branchId)] || {}; const count = branchStat.slots || 0; return <button type="button" key={branch.branchId} className={key(branch.branchId) === key(branchId) ? "active" : ""} onClick={() => setBranchId(String(branch.branchId))}><span>{branch.branchName}</span><small>{count} slot{count === 1 ? "" : "s"}{branchStat.inherited ? " default" : ""}</small><MdArrowForwardIos /></button>; })}{shownBranches.length === 0 && <p>No branches found.</p>}</div>
          </aside>

          <main className="dm-main">
            {selectedBranch ? <>
              <div className="dm-branch-header"><div><h3>{selectedBranch.branchName}</h3><p>Branch ID: {selectedBranch.branchId}<i />{branchSlots.length} delivery slots{usingGlobalSlots ? " (All Branches default)" : ""}<i />{branchOptions.length} configurations<i />Last updated: {lastUpdated ? moment(lastUpdated).format("DD MMM YYYY, hh:mm A") : "-"}</p></div><Button variant="outline-secondary" size="sm" onClick={load}><MdRefresh /> Refresh</Button></div>
              <div className="dm-method-grid">
                {methodCard(2, "Express", MdBolt, "express", express)}
                {methodCard(4, "Standard", MdLocalShipping, "standard", standard)}
                <div className="dm-method dm-method--regular"><button type="button" className="dm-method-main" onClick={addSlot}><span className="dm-method-icon"><MdCalendarToday /></span><span className="dm-method-copy"><span className="dm-method-title">Regular {status(activeSlots.length > 0, "Not configured")}</span><span>{branchSlots.length} time slots</span><small>{amountGroups.length} amount ranges</small></span><MdArrowForwardIos className="dm-arrow" /></button></div>
              </div>

              <section className="dm-regular">
                <div className="dm-section-title"><div><h3>Regular Delivery Slots</h3><p>Set up time slots, capacity and order amount range for regular delivery.</p></div><Button variant="success" size="sm" onClick={addSlot}>+ Add Slot</Button></div>
                {usingGlobalSlots && <div className="dm-inherited-note"><strong>Using All Branches default slots</strong><span>This branch has no separate Regular delivery slots. These records come from branch_id = 0. Edit a slot and choose whether to save an override for {selectedBranch.branchName} or update the All Branches default.</span></div>}
                {amountGroups.length ? <><div className="dm-amount-tabs">{amountGroups.map((group) => <button type="button" key={group.id} className={amountKey === group.id ? "active" : ""} onClick={() => setAmountKey(group.id)}>{amountText(group.min, group.max)} <span>({group.slots.length})</span></button>)}</div><div className="dm-table-wrap"><table className="dm-table"><thead><tr><th>Time Slot</th><th>Order Amount</th><th>Capacity</th><th>Status</th><th>Action</th></tr></thead><tbody>{(selectedGroup?.slots || []).map((slot) => <tr key={slot.id}><td>{timeText(slot.fromTime)} - {timeText(slot.toTime)}</td><td>{amountText(slot.minAmount, slot.maxAmount)}</td><td>{slot.orderThreshold} orders</td><td>{status(active(slot.status))}</td><td><button type="button" className="dm-edit" onClick={() => editSlot(slot)}><MdEdit /> Edit</button></td></tr>)}</tbody></table></div></> : <div className="dm-empty"><MdCalendarToday /><strong>No regular delivery slots configured</strong><span>Add the first slot for {selectedBranch.branchName}.</span><Button variant="success" size="sm" onClick={addSlot}>+ Add first slot</Button></div>}
              </section>

              {(express || standard) && <section className="dm-fees"><h3>Delivery Fee Details</h3><div className="dm-fee-grid">{[{ name: "Express", config: express }, { name: "Standard", config: standard }].map(({ name, config }) => <div key={name}><header><strong>{name}</strong><span>{config ? feesOf(config).length : 0} slabs</span></header>{config && feesOf(config).length ? feesOf(config).map((fee, index) => <div className="dm-fee-row" key={fee.id || index}><strong>{amountText(fee.min_order_value, fee.max_order_value)}</strong><span>Radius: {Number(fee.min_radius_km || 0)} - {Number(fee.max_radius_km || 0)} km</span><span>Fee: {money.format(Number(fee.fee || 0))}</span><span>Smart cart: {money.format(Number(fee.smart_cart_fee || 0))}</span><span>Threshold: {Number(fee.threshold || 0)}</span></div>) : <p>No fee slabs configured.</p>}</div>)}</div></section>}
            </> : <Alert variant="info">Select a branch to manage its delivery settings.</Alert>}
          </main>

        </div>
      )}
      <DeliveryOptionForm show={optionModal} onHide={() => setOptionModal(false)} initialData={optionData} allBranches={branches} onSave={() => { setOptionModal(false); load(); }} />
      <DeliverySlotForm show={slotModal} onHide={() => setSlotModal(false)} initialData={slotData} scopePrompt targetBranchId={Number(branchId)} targetBranchName={selectedBranch?.branchName || "this branch"} onSave={() => { setSlotModal(false); load(); }} />
    </div>
  );
};

export default DeliveryConfiguration;
